/*
 * Sake Profile — PDP section behavior
 * Spec: .planning/sake-profile/UI-SPEC.md
 *
 * Responsibilities:
 *  1. Gate the staggered score-arc reveal animation behind an
 *     IntersectionObserver so it only fires once the section enters
 *     the viewport (threshold 0.4).
 *  2. Drive the info-button tooltips: hover/focus on desktop, click
 *     toggle on mobile, single-active rule, dismiss on outside-click
 *     or Escape.
 *
 * Score-mapping is computed Liquid-side in the section template, not
 * here. This file only handles client-side UI behavior.
 */
(function () {
  'use strict';

  const ready = (cb) => {
    if (document.readyState !== 'loading') cb();
    else document.addEventListener('DOMContentLoaded', cb);
  };

  // Parallax init must wait until GSAP + ScrollTrigger are defined. Those
  // libraries are loaded as `defer` scripts AFTER `{{ content_for_layout }}`
  // in layout/theme.liquid, so they execute AFTER this file. DOMContentLoaded
  // fires after all defer scripts — at that point both globals exist.
  const afterDefers = (cb) => {
    if (document.readyState === 'complete') cb();
    else window.addEventListener('DOMContentLoaded', cb, { once: true });
  };

  function initLifestyleParallax(section) {
    // Subtle scroll-linked parallax on the right-column lifestyle image.
    // The image is overscanned by 16% in CSS so translating ±8% never
    // reveals empty space at the section edges. GSAP + ScrollTrigger are
    // loaded globally in theme.liquid — do not re-include here.
    const img = section.querySelector('.sake-profile__lifestyle img');
    if (!img) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    afterDefers(() => {
      if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;
      gsap.registerPlugin(ScrollTrigger);
      gsap.fromTo(
        img,
        { yPercent: -8 },
        {
          yPercent: 8,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
    });
  }

  function initRevealObserver(section) {
    const row = section.querySelector('.sake-profile__scores');
    if (!row) return;

    // No-op when IntersectionObserver isn't supported — final positions
    // land via the CSS `.is-revealed` rule anyway.
    if (typeof IntersectionObserver !== 'function') {
      row.classList.add('is-revealed');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            row.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(row);
  }

  function initTooltips(section) {
    const triggers = section.querySelectorAll('[data-tooltip-trigger]');
    if (triggers.length === 0) return;

    const isTouch =
      window.matchMedia && window.matchMedia('(hover: none)').matches;
    let activeTooltip = null;
    let activeTrigger = null;

    // Mobile gutter — keep tooltip this many px clear of the viewport edge.
    const TOOLTIP_GUTTER = 12;

    function clampTooltipToViewport(tooltip) {
      // Only nudge on narrow viewports where overflow is plausible. The CSS
      // media query already caps max-width to viewport - 32; this handles
      // off-center triggers (left/right score circles) that would still poke
      // past an edge after centering.
      if (!window.matchMedia('(max-width: 992px)').matches) {
        tooltip.style.removeProperty('--tooltip-shift');
        return;
      }
      tooltip.style.setProperty('--tooltip-shift', '0px');
      // Measure after layout settles.
      const rect = tooltip.getBoundingClientRect();
      let shift = 0;
      const overflowRight = rect.right - (window.innerWidth - TOOLTIP_GUTTER);
      const overflowLeft = TOOLTIP_GUTTER - rect.left;
      if (overflowRight > 0) shift = -overflowRight;
      if (overflowLeft > 0) shift = overflowLeft;
      if (shift !== 0) {
        tooltip.style.setProperty('--tooltip-shift', shift + 'px');
      }
    }

    function open(trigger, tooltip) {
      if (activeTooltip && activeTooltip !== tooltip) close();
      tooltip.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      activeTooltip = tooltip;
      activeTrigger = trigger;
      // Defer until tooltip is visible so getBoundingClientRect is meaningful.
      requestAnimationFrame(() => clampTooltipToViewport(tooltip));
    }

    function close() {
      if (!activeTooltip) return;
      activeTooltip.hidden = true;
      activeTooltip.style.removeProperty('--tooltip-shift');
      if (activeTrigger) activeTrigger.removeAttribute('aria-expanded');
      activeTooltip = null;
      activeTrigger = null;
    }

    // Re-clamp on resize while tooltip is open (orientation change, etc).
    window.addEventListener('resize', () => {
      if (activeTooltip) clampTooltipToViewport(activeTooltip);
    });

    triggers.forEach((trigger) => {
      const targetId = trigger.getAttribute('data-tooltip-target');
      if (!targetId) return;
      const tooltip = section.querySelector('#' + CSS.escape(targetId));
      if (!tooltip) return;

      trigger.setAttribute('aria-expanded', 'false');

      if (isTouch) {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          if (activeTooltip === tooltip) {
            close();
          } else {
            open(trigger, tooltip);
          }
        });
      } else {
        // Desktop: hover + keyboard focus both reveal.
        const show = () => open(trigger, tooltip);
        const hide = () => {
          if (activeTooltip === tooltip) close();
        };
        trigger.addEventListener('mouseenter', show);
        trigger.addEventListener('focus', show);
        trigger.addEventListener('mouseleave', hide);
        trigger.addEventListener('blur', hide);
        // Also support touch on desktop (hybrid devices)
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          if (activeTooltip === tooltip) {
            close();
          } else {
            open(trigger, tooltip);
          }
        });
      }
    });

    // Outside-click dismiss
    document.addEventListener('click', (e) => {
      if (!activeTooltip) return;
      if (
        activeTrigger &&
        !activeTrigger.contains(e.target) &&
        !activeTooltip.contains(e.target)
      ) {
        close();
      }
    });

    // Escape dismiss
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && activeTooltip) close();
    });
  }

  ready(function () {
    const sections = document.querySelectorAll('.sake-profile');
    sections.forEach((section) => {
      initRevealObserver(section);
      initTooltips(section);
      initLifestyleParallax(section);
    });
  });
})();
